import React, { useState, useEffect } from 'react';

import { useAuth } from '../../context/wms-context';
import config from '../../../utils/config';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Notification from '../../../components/Notification';
import logError from '../../../utils/errorUtil';
import {
    normalizePermissions,
    permissionGroups,
    buildPermissionMetadata,
    togglePermissionValue,
} from '../../utils/permission-groups';
import {
    areAllPicklistParentsSelected,
} from '../../utils/wms-permission-menu';
import WmsPermissionCheckboxList from '../WmsPermissionCheckboxList';

const permissionMetadata = buildPermissionMetadata(permissionGroups);

export default function WMSUserPermissions() {
    const ORIGIN = config.origin;
    const { currentUser } = useAuth();
    const [allEmployees, setAllEmployees] = useState();
    const [loading, setLoading] = useState(true);
    const [employeeLoading, setEmployeeLoading] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notification, setNotification] = useState({ title: '', desc: '', status: '' });

    useEffect(() => {
        // fetch get-all-employee-permissions
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };

        fetch(
            `${ORIGIN}/wms/login/get-all-employee-permissions?userId=${currentUser.authentication.userId}&userEmail=${currentUser.authentication.userEmail}`,
            requestOptions
        )
            .then((res) => res.json())
            .then((data) => {
                setAllEmployees((data.results || []).map((employee) => ({
                    ...employee,
                    WMS_Permissions__c: normalizePermissions(employee.WMS_Permissions__c || []),
                })));
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        // When the notification state changes, clear it after 5 seconds
        if (showNotification) {
            const timeoutId = setTimeout(() => {
                setShowNotification(false);
            }, 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [showNotification]);

    const handleCheckboxChange = (employeeId, permissionValue, isChecked) => {
        setAllEmployees((currentEmployees) =>
            currentEmployees.map((emp) => {
                if (emp.Id !== employeeId) return emp;

                const updatedPermissions = togglePermissionValue(
                    emp.WMS_Permissions__c || [],
                    permissionValue,
                    isChecked,
                    permissionMetadata
                );
                return { ...emp, WMS_Permissions__c: updatedPermissions };
            })
        );
    };

    const saveSingleEmployeePermissions = (employeeId, permissions) => {
        setEmployeeLoading(employeeId);
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: employeeId,
                permissions: permissions,
                ...currentUser.authentication,
            }),
        };

        fetch(`${ORIGIN}/wms/login/update-employee-permissions`, requestOptions)
            .then((res) => {
                if (!res.ok) throw new Error('Network response was not ok');
                setShowNotification(true);
                setNotification({
                    title: 'User Permissions Saved',
                    desc: 'Permissions have been saved successfully',
                    status: 'success',
                });
                setEmployeeLoading(false);
            })
            .catch((error) => {
                setShowNotification(true);
                setNotification({
                    title: 'Error saving user permissions.',
                    desc: 'Please try again.',
                    status: 'failure',
                });
                logError(error, 'saveSingleEmployeePermissions');
                setEmployeeLoading(false);
            });
    };

    const handleSelectAllPermissions = (employeeId) => {
        setAllEmployees((currentEmployees) =>
            currentEmployees.map((emp) => {
                if (emp.Id !== employeeId) return emp;

                const picklistValues = (emp.permissions || []).map((perm) => perm.value);
                const selectAll = !areAllPicklistParentsSelected(emp.WMS_Permissions__c || [], emp.permissions);
                const updatedPermissions = selectAll
                    ? normalizePermissions(picklistValues)
                    : [];
                return { ...emp, WMS_Permissions__c: updatedPermissions };
            })
        );
    };

    const handleChangePermissions = (employeeId, nextPermissions) => {
        setAllEmployees((currentEmployees) =>
            currentEmployees.map((emp) =>
                emp.Id === employeeId ? { ...emp, WMS_Permissions__c: nextPermissions } : emp
            )
        );
    };

    if (loading) return <LoadingSpinner />;

    return (
        <>
            {showNotification && (
                <Notification
                    showSavedNotification={showNotification}
                    setShowSavedNotification={setShowNotification}
                    title={notification?.title}
                    desc={notification?.desc}
                    status={notification?.status}
                />
            )}
            <h1 className='mb-4 text-2xl font-bold'>User Permissions</h1>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
                {allEmployees &&
                    allEmployees.map((employee) => (
                        <div key={employee.Id} className='relative p-4 pb-14 rounded-lg border'>
                            <div className='flex flex-wrap justify-between items-baseline gap-x-4 gap-y-1 mb-2'>
                                <h3 className='text-xl font-bold'>{employee.inscor__Employee_Name__c}</h3>
                                <p
                                    className='flex-shrink-0 text-sm text-gray-400 cursor-pointer hover:text-blue-800'
                                    onClick={() => handleSelectAllPermissions(employee.Id)}
                                >
                                    {areAllPicklistParentsSelected(
                                        employee.WMS_Permissions__c || [],
                                        employee.permissions
                                    )
                                        ? 'Unselect All Permissions'
                                        : 'Select All Permissions'}
                                </p>
                            </div>
                            <div className='w-full'>
                                <h3 className='mb-3 text-lg font-semibold'>Permissions</h3>
                                <div className='w-full ml-2'>
                                    <WmsPermissionCheckboxList
                                        permissionOptions={employee.permissions}
                                        selectedPermissions={employee.WMS_Permissions__c || []}
                                        onToggle={(permissionValue, isChecked) =>
                                            handleCheckboxChange(employee.Id, permissionValue, isChecked)
                                        }
                                        onChangePermissions={(nextPermissions) =>
                                            handleChangePermissions(employee.Id, nextPermissions)
                                        }
                                        idPrefix={`${employee.Id}-`}
                                    />
                                </div>
                            </div>
                            <button
                                type='button'
                                disabled={employeeLoading}
                                onClick={() => {
                                    saveSingleEmployeePermissions(employee.Id, employee.WMS_Permissions__c);
                                }}
                                className='absolute right-2 bottom-2 px-4 py-2 font-bold text-white rounded cursor-pointer bg-blue-primary hover:bg-blue-700 disabled:opacity-80'
                            >
                                {employeeLoading === employee.Id ? 'loading...' : 'Save'}
                            </button>
                        </div>
                    ))}
            </div>
        </>
    );
}
